import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { BemMethod, createBem, isBrowser } from '@om-fe/utils';
import { AppState, PageSeoMetadata, seoMetadataUpdater } from '@om-fe/core/core/types';
import { LiveEventRouteParams } from '@om-fe/core/core/routing/route-params';
import Metadata from '@om-fe/core/components/metadata/metadata';
import { EMRoutingContextInterface, RouteNames } from '@om-fe/core/core/routing/utils';
import { Breakpoints } from '@om-fe/core/core/store/breakpoints';
import withTranslation from '@om-fe/core/core/i18n/with-translation';
import { I18nContextInterface } from '@om-fe/core/core/i18n/utils';
import { WithMarketTemplateMappingsProps } from '@om-fe/core/components/market-template-mappings-context/with-market-template-mappings';
import BreakpointWrapper from '@om-fe/core/components/breakpoint-wrapper/breakpoint-wrapper';
import LiveEventContent from './parts/live-event-content';
import { MobileHeaderLiveEventPage } from '../components/MobileHeaderLiveEventPage/mobile-header-live-event-page';
import getConfig, { LiveEventConfig, OMFE2ConfigModel, MarketsHeaderOptions } from '@om-fe/core/utils/config';
import BottomDrawer from '@om-fe/core/components/bottom-drawer/bottom-drawer';
import LiveEventSidebarMatchList from '../components/live-event/sidebar/match-list/live-event-sidebar-match-list';
import { DEFAULT_LIVE_SIDEBAR_ODDS_EXPANDED, DEFAULT_LIVE_SIDEBAR_GROUPS_EXPANDED, DEFAULT_LIVE_SIDEBAR_EVENT_NUMBER } from '../components/match-list/utils/match-list.constants';
import { LIVE_SIDEBAR_NAV_MAIN_MARKETS } from '../components/live-event/sidebar/match-list/live-event-sidebar-match-list.constants';
import { DisciplineLiveStatus } from '@om-fe/models';
import { MatchListItemLayout } from '../components/match-list/utils/match-list.constants';
import getSlice from '@om-fe/core/utils/get-slice';
import LiveEventOutrights from './parts/live-event-outrights';
import { SCROLL_CONTAINER_TOP_CLASS } from '@om-fe/core/utils/dom';
import { setVirtualStateFocusVisible } from '@om-fe/core/core/routing/custom-routes/custom-routes.utils';
import { withSelectionBetslipData, WithSelectionBetslipDataModel } from '@om-fe/widgets/dist/widgets/betslip/with-betslip-selection-data/with-betslip-selection-data';
import { DeepLink } from '@om-fe/core/utils/deep-link';

interface LiveEventPageStoreProps {
    readonly breakpoints: Breakpoints;
    readonly lang: string;
    readonly showEventCategory?: boolean;
    readonly routeName: RouteNames;
}

type LiveEventPageProps = EMRoutingContextInterface
    & LiveEventPageStoreProps
    & RouteComponentProps<LiveEventRouteParams>
    & WithMarketTemplateMappingsProps
    & I18nContextInterface
    & WithSelectionBetslipDataModel;

interface LiveEventPageState {
    readonly metadata: PageSeoMetadata;
    readonly isDrawerOpen: boolean;
}

const bem: BemMethod = createBem('LiveEventContent');
const bemMatchDetails: BemMethod = createBem('MatchDetails');

class LiveEventPageComponent extends React.Component<LiveEventPageProps, LiveEventPageState> {
    timeoutId: NodeJS.Timeout | undefined = undefined;
    state: LiveEventPageState = {
        metadata: {
            title: '',
            keywords: '',
        },
        isDrawerOpen: false,
    };

    toggleDrawer = () => {
        this.setState({
            isDrawerOpen: !this.state.isDrawerOpen
        })
    }

    closeDrawer = () => {
        this.setState({
            isDrawerOpen: false
        })
    }

    unmounted: boolean = false;

    handleScrollCallback = () => {
        clearTimeout(this.timeoutId);
        this.timeoutId = setTimeout(() => {
            setVirtualStateFocusVisible(`.${SCROLL_CONTAINER_TOP_CLASS} article`, '.Market__Legend .Market__CollapseText');
        }, 500);
    };

    componentWillUnmount(): void {
        this.unmounted = true;
        const config: OMFE2ConfigModel = getConfig();
        if (isBrowser() && config.virtualState) {
            const scrollableElementList: NodeListOf<Element> = document.querySelectorAll(`.${SCROLL_CONTAINER_TOP_CLASS}`);
            scrollableElementList.forEach((element) => {
                element.removeEventListener('scroll', this.handleScrollCallback);
            });
        }
    }

    handleScroll() {
        const config: OMFE2ConfigModel = getConfig();
        if (isBrowser() && config.virtualState) {
            const scrollableElementList: NodeListOf<Element> = document.querySelectorAll(`.${SCROLL_CONTAINER_TOP_CLASS}`);
            scrollableElementList.forEach((element) => {
                element.addEventListener('scroll', this.handleScrollCallback);
            });
        }
    }

    onMetadataUpdate: seoMetadataUpdater = ({ title = '', keywords = '' }) => {
        const metadata = { title, keywords };

        if (!this.unmounted) {
            this.setState({ metadata });
            this.handleScroll();
        }
    };

    componentDidMount() {
        const { match: { params: { id, bettingOfferIds, stake } }, addSelection, setMasterStake, setSingleSelectionStake } = this.props;
        DeepLink({ bettingOfferIds, stake, id, addSelection, setSingleSelectionStake, setMasterStake });
    }

    componentDidUpdate(prevProps: Readonly<LiveEventPageProps>) {
        const { isDrawerOpen } = this.state;
        const { breakpoints, match: { params: { name: currentMatchName }}} = this.props;
        const previousMatchName = prevProps.match.params.name;

        if (isDrawerOpen) {
            if (breakpoints.large || breakpoints.xl) {
                this.closeDrawer()
            }

            if (previousMatchName !== currentMatchName) {
                this.closeDrawer()
            }
        }
    }

    render() {
        const {
            match: { params: { id, disciplineId } },
            marketTemplateMappings,
            showEventCategory,
            routeName,
        } = this.props;

        const { metadata: { keywords, title }, isDrawerOpen } = this.state;
        
        const config: OMFE2ConfigModel = getConfig();
        const matchListLayout: MatchListItemLayout = config.matchListConfig?.commonConfig?.matchListItemLayout;
        const configLiveEvent: LiveEventConfig | undefined = config.matchListConfig && config.matchListConfig.liveEvent;
        const numberOfOddsExpanded = (configLiveEvent?.numberOfOddsExpanded) ?? DEFAULT_LIVE_SIDEBAR_ODDS_EXPANDED;
        const numberOfGroupsExpanded = (configLiveEvent && configLiveEvent.numberOfGroupsExpanded) ?? DEFAULT_LIVE_SIDEBAR_GROUPS_EXPANDED;
        const eventsNumber = (configLiveEvent && configLiveEvent.eventsNumber) ?? DEFAULT_LIVE_SIDEBAR_EVENT_NUMBER;
        const marketsHeaderOptions: MarketsHeaderOptions | undefined = configLiveEvent && configLiveEvent.marketsHeaderOptions;
        
        return (
            <>
                <Metadata
                    title={title}
                    keywords={keywords}
                    description={title}
                />
                <BreakpointWrapper breakpoints={[Breakpoints.Large, Breakpoints.XL]} action="hide">
                    <MobileHeaderLiveEventPage
                        matchId={id}
                        bem={bem}
                        isDrawerOpen={isDrawerOpen}
                        toggleDrawer={this.toggleDrawer}
                        disciplineId={disciplineId}
                        routeName={routeName}
                    />
                    {isDrawerOpen && (
                        <BottomDrawer onClose={this.toggleDrawer}>
                            <LiveEventSidebarMatchList
                                mainMarketsLimit={LIVE_SIDEBAR_NAV_MAIN_MARKETS}
                                activeMatchId={id || null}
                                disciplineId={disciplineId}
                                eventItemExtraClassname={bem({
                                    element: 'EventItem',
                                    extra: 'LiveEventItem',
                                })}
                                extraClassName={bem('MatchList')}
                                instanceId={`LiveEventSidebar-Live-event-drawer`}
                                liveStatus={DisciplineLiveStatus.LIVE}
                                limitNumberOfEvents={eventsNumber}
                                numberOfGroupsExpanded={numberOfGroupsExpanded}
                                numberOfOddsExpanded={numberOfOddsExpanded}
                                showMarketsName={false}
                                marketsHeaderOptions={marketsHeaderOptions}
                                itemLayout={matchListLayout}
                                showMatchListHeader={true}
                                routeName={RouteNames.liveEvent}
                                showMarketInMatchListGroupHeader={false}
                            />
                        </BottomDrawer>  
                    )}
                </BreakpointWrapper>
                <div className={bem()}>
                    <div className={bemMatchDetails()}>
                        {id && disciplineId && routeName === RouteNames.liveOutright
                            ?
                                <LiveEventOutrights
                                    tournamentId={id}
                                    disciplineId={disciplineId}
                                    marketTemplateMappings={marketTemplateMappings}
                                    showEventCategory={showEventCategory}
                                />
                            :
                                <LiveEventContent
                                    id={id}
                                    disciplineId={disciplineId}
                                    marketTemplateMappings={marketTemplateMappings}
                                    onMetadataUpdate={this.onMetadataUpdate}
                                    showEventCategory={showEventCategory}
                                />
                        }
                    </div>
                </div>
            </>
        );
    }
}

const LiveEventPage: React.ComponentType<LiveEventPageProps> = compose(
    withRouter,
    withSelectionBetslipData<LiveEventPageProps>(),
    withTranslation<LiveEventPageProps>(),
    connect<LiveEventPageStoreProps, null, LiveEventPageProps, AppState>(
        (state: AppState): LiveEventPageStoreProps => {
            const sliceName: string = 'live-LiveEventSidebar-Live-event-left-sidebar';
            const sliceSport = getSlice(state, sliceName);

            return {
                breakpoints: state.breakpoints,
                lang: state.config.lang,
                showEventCategory: sliceSport?.discipline?.showEventCategory,
                routeName: state.routing.name,
            };
        },
    ),
)(LiveEventPageComponent);
export default LiveEventPage;